import React, { useEffect, useRef, useState } from 'react';

// Draws ISO-style dimension lines around its child and keeps them true to the rendered size.
// The page measures itself: that is the whole joke, and the whole point.
export default function Dimensioned({ children, tol = '±0.50' }) {
  const ref = useRef(null);
  const shownRef = useRef(0);
  const [{ w, h }, setSize] = useState({ w: 0, h: 0 });
  const [shown, setShown] = useState(0);

  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setSize({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  // Tween the displayed width toward the measured one (1s, ease-out cubic).
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      shownRef.current = w;
      setShown(w);
      return undefined;
    }
    const from = shownRef.current;
    const t0 = performance.now();
    let raf;
    const step = (now) => {
      const t = Math.min(1, (now - t0) / 1000);
      shownRef.current = from + (w - from) * (1 - (1 - t) ** 3);
      setShown(shownRef.current);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [w]);

  return (
    <div ref={ref} className="mx-dimmed">
      {children}
      {w > 0 && (
        <>
          <svg className="mx-dim" aria-hidden="true" width={w} height="46" style={{ left: 0, top: -46 }}>
            <line x1="0.5" y1="44" x2="0.5" y2="6" />
            <line x1={w - 0.5} y1="44" x2={w - 0.5} y2="6" />
            <line x1="9" y1="14" x2={w - 9} y2="14" />
            <polygon points="0.5,14 9,11.5 9,16.5" />
            <polygon points={`${w - 0.5},14 ${w - 9},11.5 ${w - 9},16.5`} />
            <text x={w / 2} y="9" textAnchor="middle" className="mx-label">
              {shown.toFixed(2)} <tspan fill="var(--mx-ink-3)">{tol}</tspan>
            </text>
          </svg>
          <svg className="mx-dim mx-dim-v" aria-hidden="true" width="58" height={h} style={{ top: 0, right: -58 }}>
            <line x1="2" y1="0.5" x2="40" y2="0.5" />
            <line x1="2" y1={h - 0.5} x2="40" y2={h - 0.5} />
            <line x1="32" y1="9" x2="32" y2={h - 9} />
            <polygon points="32,0.5 29.5,9 34.5,9" />
            <polygon points={`32,${h - 0.5} 29.5,${h - 9} 34.5,${h - 9}`} />
            <text x="40" y={h / 2} dominantBaseline="middle" className="mx-label">{h.toFixed(2)}</text>
          </svg>
        </>
      )}
    </div>
  );
}
